import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Chip,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  AlertTitle,
  Switch,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Shield as ShieldIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { topupRatesService, CashLimit } from '../../../services';

const ESCOPO_LABEL: Record<string, string> = {
  CLIENTE: 'Cliente',
  OPERADOR: 'Posto / operador',
};

const KYC_LABEL: Record<string, string> = {
  BASIC: 'Não verificado',
  STANDARD: 'Verificado',
  ENHANCED: 'Verificação reforçada',
};

const NUM_FIELDS = [
  { key: 'perTxMaxCVE',             label: 'Máx. por operação', unit: 'CVE' },
  { key: 'dailyMaxCVE',             label: 'Máx. por dia',      unit: 'CVE' },
  { key: 'monthlyMaxCVE',           label: 'Máx. por mês',      unit: 'CVE' },
  { key: 'dailyCountMax',           label: 'Operações/dia',     unit: '' },
  { key: 'structuringThresholdCVE', label: 'Limiar de alerta',  unit: 'CVE' },
  { key: 'structuringMinCount',     label: 'Repetições p/ alerta', unit: '' },
] as const;

type Draft = Record<number, Record<string, string>>;

const cve = (n: number) => n.toLocaleString('pt-PT');

/**
 * Limites de numerário.
 *
 * Separado das taxas de propósito: as taxas versionam-se com o contrato, os
 * limites são controlo de risco e mudam sem renegociar nada.
 */
export default function CashLimitsPanel() {
  const [limits, setLimits] = useState<CashLimit[]>([]);
  const [draft, setDraft] = useState<Draft>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await topupRatesService.limits();
      setLimits(rows);
      setDraft({});
    } catch (err: any) {
      setError(
        err?.response?.data?.error ??
        'Não foi possível carregar os limites. Correr a migração run_20260804_cash_limits.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const valueOf = (row: CashLimit, key: string): string => {
    const d = draft[row.id]?.[key];
    if (d != null) return d;
    return String((row as any)[key] ?? '');
  };

  const isDirty = (row: CashLimit): boolean => {
    const d = draft[row.id];
    if (!d) return false;
    return Object.entries(d).some(([k, v]) => String((row as any)[k] ?? '') !== v);
  };

  const setField = (id: number, key: string, value: string) => {
    setDraft((prev) => ({ ...prev, [id]: { ...(prev[id] ?? {}), [key]: value } }));
  };

  const validate = (row: CashLimit): string | null => {
    const nums: Record<string, number> = {};
    for (const f of NUM_FIELDS) {
      const raw = valueOf(row, f.key);
      const n = Number(raw);
      if (!Number.isInteger(n) || n < 0) return `${f.label} tem de ser um inteiro não negativo.`;
      nums[f.key] = n;
    }
    if (nums.perTxMaxCVE > nums.dailyMaxCVE) return 'O máximo por operação não pode exceder o diário.';
    if (nums.dailyMaxCVE > nums.monthlyMaxCVE) return 'O máximo diário não pode exceder o mensal.';
    return null;
  };

  const save = async (row: CashLimit) => {
    const invalid = validate(row);
    if (invalid) { setError(invalid); return; }

    setSavingId(row.id);
    setError(null);
    setSuccess(null);
    try {
      const patch: any = {};
      for (const f of NUM_FIELDS) patch[f.key] = Number(valueOf(row, f.key));
      patch.structuringWindowHours = row.structuringWindowHours;
      patch.ativo = row.ativo;

      await topupRatesService.updateLimit(row.id, patch);
      setSuccess(`Limites de ${ESCOPO_LABEL[row.escopo]}${row.kycLevel ? ` / ${KYC_LABEL[row.kycLevel]}` : ''} actualizados.`);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Não foi possível guardar os limites.');
    } finally {
      setSavingId(null);
    }
  };

  const toggleActive = async (row: CashLimit) => {
    setSavingId(row.id);
    setError(null);
    try {
      await topupRatesService.updateLimit(row.id, { ativo: !row.ativo } as any);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Não foi possível alterar o estado.');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShieldIcon fontSize="small" /> Limites de numerário
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aplicam-se ao dinheiro entregue em mão, tanto no carregamento de carteira como no de pulseiras.
          </Typography>
        </Box>

        <Alert severity="info" icon={<InfoIcon />}>
          <AlertTitle>Um tecto por operação não trava fraccionamento</AlertTitle>
          Quem quiser mover 500&nbsp;000&nbsp;CVE faz onze carregamentos de 45&nbsp;000. O que trava é a soma
          do dia e do mês. O limiar de alerta e as repetições definem quando um padrão desses gera
          alerta de compliance para revisão — sem bloquear o balcão.
        </Alert>

        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell>Escopo</TableCell>
                {NUM_FIELDS.map((f) => (
                  <TableCell key={f.key} align="right">{f.label}</TableCell>
                ))}
                <TableCell align="center">Activo</TableCell>
                <TableCell align="right">Acções</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {limits.map((row) => {
                const dirty = isDirty(row);
                const busy = savingId === row.id;
                return (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {ESCOPO_LABEL[row.escopo] ?? row.escopo}
                      </Typography>
                      {row.kycLevel && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={KYC_LABEL[row.kycLevel] ?? row.kycLevel}
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </TableCell>

                    {NUM_FIELDS.map((f) => (
                      <TableCell key={f.key} align="right">
                        <TextField
                          size="small"
                          value={valueOf(row, f.key)}
                          onChange={(e) => setField(row.id, f.key, e.target.value)}
                          disabled={busy}
                          inputProps={{
                            style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', width: '6.5rem' },
                          }}
                        />
                      </TableCell>
                    ))}

                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={row.ativo}
                        disabled={busy}
                        onChange={() => toggleActive(row)}
                      />
                    </TableCell>

                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={dirty ? 'Guardar' : 'Nada alterado'}>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={!dirty || busy}
                            onClick={() => save(row)}
                          >
                            {busy ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Descartar">
                        <span>
                          <IconButton
                            size="small"
                            disabled={!dirty || busy}
                            onClick={() => setDraft((p) => { const n = { ...p }; delete n[row.id]; return n; })}
                          >
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button onClick={load} disabled={savingId != null}>Recarregar</Button>
          <Typography variant="caption" color="text.secondary">
            Um cliente sem registo de KYC é tratado como não verificado — o escalão mais restritivo.
          </Typography>
        </Box>

        {limits.some((l) => l.escopo === 'CLIENTE' && l.ativo) && (
          <Typography variant="caption" color="text.secondary">
            Exemplo: um cliente verificado pode carregar até{' '}
            {cve(limits.find((l) => l.kycLevel === 'STANDARD')?.dailyMaxCVE ?? 0)} CVE por dia,
            em no máximo {limits.find((l) => l.kycLevel === 'STANDARD')?.dailyCountMax ?? 0} operações.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
